import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { Shell, Head, TotalRow, useEsc, useRowNav, navRowCls, navRowProps, fmtV, fmtQ, fmtD } from "./ReportShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const r0 = (n) => (n ? fmtV(n) : "");

export function CashFlowView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/reports/cash-flow`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const max = Math.max(...data.rows.map((r) => Math.max(r.inflow, r.outflow)), 1);
  return (
    <Shell title="Cash / Funds Flow" onBack={onClose} testid="cash-flow-view"
      subtitle={<><span>Month-wise inflow &amp; outflow (vouchers + bank)</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Month" }, { t: "Inflow", w: "w-[170px]", right: true }, { t: "Outflow", w: "w-[170px]", right: true },
        { t: "Net Flow", w: "w-[170px]", right: true }, { t: "Cumulative", w: "w-[170px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="cf-rows">
        {data.rows.map((r, i) => (
          <div key={r.month} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`cf-row-${r.month}`}>
            <div className="flex-1 px-3 py-0.5 font-semibold">{r.label}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r0(r.inflow)}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r0(r.outflow)}</div>
            <div className={`w-[170px] px-2 py-0.5 text-right font-semibold ${r.net < 0 ? "text-red-700" : r.net > 0 ? "text-emerald-700" : ""}`}>{r0(r.net)}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r0(r.running)}</div>
          </div>
        ))}
      </div>
      <div className="px-4 pt-2 pb-1 border-t border-slate-300" data-testid="cf-graph">
        <div className="flex items-end gap-1 h-16">
          {data.rows.map((r) => (
            <div key={r.month} className="flex-1 flex items-end justify-center gap-0.5">
              <div title={`In ${r.inflow}`} className="w-2.5 rounded-t-full" style={{ height: `${(r.inflow / max) * 56}px`, background: "linear-gradient(180deg,#14b8a6,#0f766e)" }} />
              <div title={`Out ${r.outflow}`} className="w-2.5 rounded-t-full" style={{ height: `${(r.outflow / max) * 56}px`, background: "linear-gradient(180deg,#fbbf24,#d97706)" }} />
            </div>
          ))}
        </div>
        <div className="flex gap-1 text-[9px] font-bold text-slate-600">
          {data.rows.map((r) => <div key={r.month} className="flex-1 text-center">{r.label.slice(0, 3)}</div>)}
        </div>
      </div>
      <TotalRow testid="cf-totals" cells={[{ t: "Grand Total" }, { t: r0(data.totals.inflow), w: "w-[170px]", right: true },
        { t: r0(data.totals.outflow), w: "w-[170px]", right: true }, { t: r0(data.totals.net), w: "w-[340px]", right: true }]} />
    </Shell>
  );
}

export function ReceiptsPaymentsView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  useEffect(() => {
    axios.get(`${API}/reports/receipts-payments`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const Side = ({ title, rows, total, testid }) => (
    <div className="flex-1 flex flex-col border border-slate-300 rounded-sm bg-white/60 overflow-hidden" data-testid={testid}>
      <div className="text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>{title}</div>
      <div className="flex-1 p-2">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between text-sm py-1 border-b border-dashed border-slate-200">
            <span className="font-semibold text-slate-800">{r.name}</span>
            <span>{fmtV(r.amount)}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No entries</div>}
      </div>
      <div className="flex justify-between px-2 py-1.5 font-black text-sm border-t-2 border-slate-700 bg-[#f1e9d6]">
        <span>Total</span><span>{fmtV(total) || "0.00"}</span>
      </div>
    </div>
  );
  return (
    <Shell title="Receipts and Payments" onBack={onClose} testid="receipts-payments-view"
      subtitle={<><span>Account-wise summary of money received &amp; paid</span><span>{data.period}</span></>}>
      <div className="flex-1 flex gap-4 px-4 py-3 overflow-y-auto">
        <Side title="Receipts" rows={data.receipts} total={data.totals.receipts} testid="rp-receipts" />
        <Side title="Payments" rows={data.payments} total={data.totals.payments} testid="rp-payments" />
      </div>
      <TotalRow testid="rp-totals" cells={[{ t: "Net (Receipts − Payments)" },
        { t: fmtV(data.totals.net) || "0.00", w: "w-[220px]", right: true }]} />
    </Shell>
  );
}

export function ListOfAccountsView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  useEffect(() => {
    axios.get(`${API}/reports/list-of-accounts`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const c = data.counts;
  return (
    <Shell title="List of Accounts (Chart of Accounts)" onBack={onClose} testid="list-accounts-view"
      subtitle={<><span>{c.groups} groups · {c.ledgers} ledgers · {c.debtors} debtors · {c.creditors} creditors · {c.stock_items} stock items</span><span /></>}>
      <div className="flex-1 flex gap-4 px-4 py-2 overflow-hidden">
        <div className="flex-1 overflow-y-auto border border-slate-300 rounded-sm bg-white/60" data-testid="loa-groups">
          <div className="sticky top-0 text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>Groups &amp; Ledgers</div>
          {data.groups.map((g, i) => (
            <div key={i} className="px-3 py-0.5">
              <div className="text-sm font-bold text-slate-900">{g.name} <span className="text-[10px] font-normal text-slate-500 italic">under {g.parent}</span></div>
              {g.ledgers.map((l, j) => (
                <div key={j} className="pl-5 text-sm text-slate-700">· {l}</div>
              ))}
            </div>
          ))}
        </div>
        <div className="w-[300px] flex flex-col gap-2 overflow-y-auto">
          <div className="border border-slate-300 rounded-sm bg-white/60 flex-1 overflow-y-auto" data-testid="loa-debtors">
            <div className="sticky top-0 text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>Sundry Debtors ({c.debtors})</div>
            {data.debtors.map((p, i) => <div key={i} className="px-3 py-0.5 text-sm text-slate-800">{p}</div>)}
          </div>
          <div className="border border-slate-300 rounded-sm bg-white/60 flex-1 overflow-y-auto" data-testid="loa-creditors">
            <div className="sticky top-0 text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>Sundry Creditors ({c.creditors})</div>
            {data.creditors.map((p, i) => <div key={i} className="px-3 py-0.5 text-sm text-slate-800">{p}</div>)}
          </div>
        </div>
      </div>
    </Shell>
  );
}

export function ExceptionReportsView({ onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const onEnter = useCallback((i) => {
    const r = data && data.overdue_invoices[i];
    if (r && r.id) navigate(`/invoices/${r.id}/edit?return=daybook`);
  }, [data, navigate]);
  const [sel, setSel] = useRowNav(data ? data.overdue_invoices.length : 0, onEnter);
  useEffect(() => {
    axios.get(`${API}/reports/exceptions`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const c = data.counts;
  const Sec = ({ title, count, children, testid }) => (
    <div className="border border-slate-300 rounded-sm bg-white/60 overflow-hidden" data-testid={testid}>
      <div className="flex justify-between px-3 py-1 text-white text-xs font-bold tracking-widest uppercase" style={{ background: count ? "#b91c1c" : "#0f766e" }}>
        <span>{title}</span><span>{count} found</span>
      </div>
      <div className="max-h-56 overflow-y-auto">{children}</div>
    </div>
  );
  return (
    <Shell title="Exception Reports" onBack={onClose} testid="exception-reports-view"
      subtitle={<><span>Items needing attention — auto-detected</span><span>{data.period}</span></>}>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        <Sec title="Overdue Invoices (unpaid past due date)" count={c.overdue_invoices} testid="ex-overdue">
          {data.overdue_invoices.map((r, i) => (
            <div key={i} onClick={() => r.id && navigate(`/invoices/${r.id}/edit?return=daybook`)} {...navRowProps(i, sel, setSel)}
              className={`flex items-center text-sm text-slate-900 cursor-pointer px-2 ${navRowCls(sel === i)}`}>
              <div className="w-[110px] py-0.5 font-semibold">{r.vch_no}</div>
              <div className="flex-1 py-0.5">{r.party}</div>
              <div className="w-[110px] py-0.5">{fmtD(r.due_date)}</div>
              <div className="w-[140px] py-0.5 text-right font-semibold text-red-700">{fmtV(r.amount)}</div>
            </div>
          ))}
          {c.overdue_invoices === 0 && <div className="px-3 py-2 text-xs text-slate-500">No overdue invoices — all clear</div>}
        </Sec>
        <Sec title="Negative Stock Items" count={c.negative_stock} testid="ex-negstock">
          {data.negative_stock.map((r, i) => (
            <div key={i} className="flex items-center text-sm text-slate-900 px-2">
              <div className="flex-1 py-0.5 font-semibold">{r.name}</div>
              <div className="w-[140px] py-0.5 text-right font-semibold text-red-700">{fmtQ(r.qty)} {r.unit}</div>
            </div>
          ))}
          {c.negative_stock === 0 && <div className="px-3 py-2 text-xs text-slate-500">No negative stock — all clear</div>}
        </Sec>
        <Sec title="Unreconciled Bank Entries" count={c.unreconciled_bank} testid="ex-unrec">
          {data.unreconciled_bank.map((r, i) => (
            <div key={i} className="flex items-center text-sm text-slate-900 px-2">
              <div className="w-[110px] py-0.5">{fmtD(r.date)}</div>
              <div className="flex-1 py-0.5">{r.description}</div>
              <div className="w-[130px] py-0.5 text-right">{r.credit ? fmtV(r.credit) : ""}</div>
              <div className="w-[130px] py-0.5 text-right text-red-700">{r.debit ? fmtV(r.debit) : ""}</div>
            </div>
          ))}
          {c.unreconciled_bank === 0 && <div className="px-3 py-2 text-xs text-slate-500">All bank entries reconciled</div>}
        </Sec>
      </div>
      <TotalRow testid="ex-totals" cells={[{ t: "Overdue amount pending" },
        { t: fmtV(c.overdue_amount) || "0.00", w: "w-[220px]", right: true }]} />
    </Shell>
  );
}
